import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  LinearProgress,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  useTheme,
  useMediaQuery,
  Paper,
  CircularProgress
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Event,
  AttachMoney,
  CalendarToday,
  CreditCard,
  AccountBalance,
  Refresh,
  Today,
  LocationOn
} from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Area, AreaChart } from 'recharts';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import CakeIcon from '@mui/icons-material/Cake';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TodayIcon from '@mui/icons-material/Today';
import HttpsIcon from '@mui/icons-material/Https';

import LoadingInformation from '../../Components/LoadingInformation';

const safeParseFloat = (value, defaultValue = 0) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value.replace(/,/g, ''));
    return isNaN(parsed) ? defaultValue : parsed;
  }
  return defaultValue;
};

function Dashboard() {
  const [dashboardData, setDashboardData] = useState({
    totalEvents: 0,
    totalMonthlyEvents: 0,
    lastMonthEvents: 0,
    currentMonthRevenue: 0,
    lastMonthRevenue: 0,
    totalRevenue: 0,
    pendingEvents: 0,
    completedEvents: 0,
    dressInventory: 0,
    suitInventory: 0,
    recentEvents: [],
    monthlyRevenue: [],
    eventsByType: [],
    paymentStatus: [],
    upcomingEvents: [],
    inventoryStatus: [],
    recentPayments: [],
    birthdaysThisMonth: []
  });
  const [permissionError, setPermissionError] = useState(false);
  const [loading, setLoading] = useState(true);

  // Responsive hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const navigate = useNavigate();

  const token = localStorage.getItem('token');

  // Función para obtener el nombre del mes actual
  const getCurrentMonthName = () => {
    const monthNames = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    const currentDate = new Date();
    return monthNames[currentDate.getMonth()];
  };

  const navigateToPath = (path) => {
    navigate(path);
  };

  // Función para obtener el año actual
  const getCurrentYear = () => {
    return new Date().getFullYear();
  };

  // Función para procesar datos mensuales
  function processMonthlyRevenue(payments, events) {
    // Crear objeto para almacenar datos por mes
    const monthlyData = {};

    // Nombres de meses en español
    const monthNames = [
      'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
      'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'
    ];

    // Inicializar todos los meses del año actual
    const currentYear = new Date().getFullYear();
    for (let i = 0; i < 12; i++) {
      const monthKey = `${currentYear}-${String(i + 1).padStart(2, '0')}`;
      monthlyData[monthKey] = {
        month: monthNames[i],
        revenue: 0,
        events: 0
      };
    }

    // Procesar pagos para obtener ingresos por mes
    payments.forEach(payment => {
      const date = new Date(payment.transactionDate);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      if (monthlyData[monthKey]) {
        monthlyData[monthKey].revenue += safeParseFloat(payment.totalAmountReceived);
      }
    });

    // Procesar eventos para obtener cantidad por mes
    events.forEach(event => {
      const date = new Date(event.eventDateStart);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      if (monthlyData[monthKey]) {
        monthlyData[monthKey].events += 1;
      }
    });

    // Convertir objeto a array y filtrar solo meses con datos
    return Object.values(monthlyData).filter(month => month.revenue > 0 || month.events > 0);
  }

  // Procesar eventos por tipo
  function processEventsByType(events) {
    const eventTypes = {
      'XV Años': 0,
      'Bodas': 0,
      'Alquiler': 0
    };

    events.forEach(event => {
      switch (event.eventType) {
        case 'inside':
          if (event.XVID == null) {
            eventTypes['Bodas']++;
          }
          else {
            eventTypes['XV Años']++;
          }
          break;
        case 'outside':
          if (event.XVID == null) {
            eventTypes['Bodas']++;
          }
          else {
            eventTypes['XV Años']++;
          }
          break;
        case 'rental':
          eventTypes['Alquiler']++;
          break;
        default:
          eventTypes['XV Años']++;
      }
    });

    const total = Object.values(eventTypes).reduce((sum, count) => sum + count, 0);

    return [
      {
        name: 'XV Años',
        value: total > 0 ? Math.round((eventTypes['XV Años'] / total) * 100) : 0,
        color: '#f759ab'
      },
      {
        name: 'Bodas',
        value: total > 0 ? Math.round((eventTypes['Bodas'] / total) * 100) : 0,
        color: '#e91e63'
      },
      {
        name: 'Alquiler',
        value: total > 0 ? Math.round((eventTypes['Alquiler'] / total) * 100) : 0,
        color: '#9c27b0'
      }
    ].filter(item => item.value > 0); // Filtrar valores cero
  }

  function processRecentPayment(payments) {
    // Obtener pagos de los últimos 5 días
    const fiveDaysAgo = new Date();
    fiveDaysAgo.setDate(fiveDaysAgo.getDate() - 5);
    
    return payments.map(payment => ({
        id: payment.transactionID, // Cambié de paymentID a transactionID según tu estructura
        client: payment.clientName || `Cliente #${payment.invoiceID}`, // Usar clientName si existe, sino usar invoiceID
        amount: safeParseFloat(payment.totalAmountReceived), // Cambié de amount a totalAmountReceived
        method: payment.paymentMethod === 'efectivo' ? 'Efectivo' : 
                payment.paymentMethod === 'transferencia' ? 'Transferencia' : 'Tarjeta',
        date: new Date(payment.createdAt).toLocaleDateString('es-DO', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        })
      }));
  }

  function getCurrentMonthEventsCount(events) {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth(); // 0-11

    return events.filter(event => {
      const eventDate = new Date(event.eventDateStart);
      return eventDate.getFullYear() === currentYear && 
              eventDate.getMonth() === currentMonth;
    }).length;
  }

  function getLastMonthEventsCount(events) {
    const currentDate = new Date();
    const currentDay = currentDate.getDate(); // Día actual del mes (1-31)
    
    // Calcular el mes y año anterior
    const lastMonth = currentDate.getMonth() === 0 ? 11 : currentDate.getMonth() - 1;
    const lastMonthYear = lastMonth === 11 ? currentDate.getFullYear() - 1 : currentDate.getFullYear();
    
    // Crear fechas de inicio y fin del período
    const startDate = new Date(lastMonthYear, lastMonth, 1); // Primer día del mes pasado
    const endDate = new Date(lastMonthYear, lastMonth, currentDay); // Hasta el mismo día del mes pasado

    return events.filter(event => {
      const eventDate = new Date(event.eventDateStart);
      return eventDate >= startDate && eventDate <= endDate;
    }).length;
  }

  // Función para obtener ingresos del mes actual
  function getCurrentMonthRevenue(payments) {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth(); // 0-11

    return payments
      .filter(payment => {
        const paymentDate = new Date(payment.transactionDate);
        return paymentDate.getFullYear() === currentYear && 
               paymentDate.getMonth() === currentMonth;
      })
      .reduce((total, payment) => {
        return total + safeParseFloat(payment.totalAmountReceived);
      }, 0);
  }

  // Función para obtener ingresos del mes anterior hasta el día actual
  function getLastMonthRevenue(payments) {
    const currentDate = new Date();
    const currentDay = currentDate.getDate(); // Día actual del mes (1-31)
    
    // Calcular el mes y año anterior
    const lastMonth = currentDate.getMonth() === 0 ? 11 : currentDate.getMonth() - 1;
    const lastMonthYear = lastMonth === 11 ? currentDate.getFullYear() - 1 : currentDate.getFullYear();
    
    // Crear fechas de inicio y fin del período
    const startDate = new Date(lastMonthYear, lastMonth, 1); // Primer día del mes pasado
    const endDate = new Date(lastMonthYear, lastMonth, currentDay); // Hasta el mismo día del mes pasado
    
    return payments
      .filter(payment => {
        const paymentDate = new Date(payment.transactionDate);
        return paymentDate >= startDate && paymentDate <= endDate;
      })
      .reduce((total, payment) => {
        return total + safeParseFloat(payment.totalAmountReceived);
      }, 0);
  }

  // Función para obtener datos del dashboard
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setPermissionError(false);
      // Aquí irían las llamadas reales a la API

      const response = await fetch('https://dprincess-training.servehttp.com/dashboard', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 403) {
        setPermissionError(true);
        setLoading(false);
        return;
      }
      if (!response.ok) {
        throw new Error('Error al obtener datos del dashboard');
      }

      const data = await response.json();

      let totalEvents = data.events.length;
      if (totalEvents === null) totalEvents = 0;
      let totalMonthlyEvents = getCurrentMonthEventsCount(data.events);
      if (totalMonthlyEvents === null) totalMonthlyEvents = 0;
      let lastMonthEvents = getLastMonthEventsCount(data.events);
      if (lastMonthEvents === null) lastMonthEvents = 0;
      
      // Calcular ingresos del mes actual y anterior
      const currentMonthRevenue = getCurrentMonthRevenue(data.yearPayments);
      const lastMonthRevenue = getLastMonthRevenue(data.yearPayments);
      
      let totalRevenue = 0;
      data.yearPayments.forEach(payment => {
        totalRevenue += safeParseFloat(payment.totalAmountReceived);
      });
      totalRevenue = totalRevenue.toLocaleString('es-DO', { style: 'currency', currency: 'DOP' });
      
      const { availableDresses, longDressesWedding, longDressesXV, shortDressesXV, totalDresses } = data.dressInventoryCount;
      const { PendingLongDressesWeddingCount: pendingDW, PendingLongDressesXVCount: pendingDXV, PendingShortDressesXVCount: pendingSXV } = data.pendingDresses;

      const totalMenSuits = data.suitInventoryCount[0].total;
      const totalChildrenSuits = data.suitInventoryCount[1].total; 
      const { PendingMensSuitsCount: pendingMS, PendingChildrenSuitsCount: pendingCS } = data.pendingSuits;

      const dressInventory = totalDresses;   
      const suitInventory = totalMenSuits + totalChildrenSuits;
      let recentEvents = [];
      if (data.events.length > 0) {
        // Obtener eventos de los últimos 15 días
        const fifteenDaysAgo = new Date();
        fifteenDaysAgo.setDate(fifteenDaysAgo.getDate() - 15);
        
        recentEvents = data.events
          .filter(event => {
            const eventDate = new Date(event.eventDateStart);
            return eventDate >= fifteenDaysAgo && eventDate <= new Date();
          })
          .sort((a, b) => new Date(b.eventDateStart) - new Date(a.eventDateStart)) // Más recientes primero
          .map(event => ({
            id: event.eventID,
            title: event.eventTitle,
            date: event.eventDateStart + ' - ' + event.eventDateEnd,
            status: event.status,
            client: event.clientName
          }));
      } 
      const monthlyRevenue = processMonthlyRevenue(data.yearPayments, data.events);
      // Procesar tipos de eventos
      const eventsByType = processEventsByType(data.events);
      
      // Procesar estado de pagos - asegurar que siempre tengamos datos válidos
      const paidInvoices = safeParseFloat(data.paymentStatusCounts?.paidInvoices, 0);
      const pendingInvoices = safeParseFloat(data.paymentStatusCounts?.pendingInvoices, 0);
      const totalInvoices = paidInvoices + pendingInvoices;
      
      let paymentStatus = [];
      if (totalInvoices > 0) {
        paymentStatus = [
          { name: 'Pagados', value: paidInvoices, color: '#4caf50' },
          { name: 'Pendientes', value: pendingInvoices, color: '#ff9800' }
        ].filter(item => item.value > 0);
      } else {
        // Datos de ejemplo si no hay facturas
        paymentStatus = [
          { name: 'Sin datos', value: 100, color: '#e0e0e0' }
        ];
      }
      let upcomingEvents = data.upcomingEvents.map(event => ({
        id: event.eventID,
        title: event.eventTitle,
        date: new Date(event.eventDateStart).toISOString().split('T')[0],
        time: `${new Date(event.eventDateStart).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })} - ${new Date(event.eventDateEnd).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`,
        location: event.eventLocation
      }));
      const inventoryStatus = [
          { category: 'Vestidos de XV Cortos', available: safeParseFloat(shortDressesXV) - pendingSXV, total: safeParseFloat(shortDressesXV), percentage: ((safeParseFloat(shortDressesXV) - pendingSXV) / safeParseFloat(shortDressesXV) * 100) },
          { category: 'Vestidos de XV Largos', available: safeParseFloat(longDressesXV) - pendingDXV, total: safeParseFloat(longDressesXV), percentage: ((safeParseFloat(longDressesXV) - pendingDXV) / safeParseFloat(longDressesXV) * 100) },
          { category: 'Vestidos de Boda', available: safeParseFloat(longDressesWedding) - pendingDW, total: safeParseFloat(longDressesWedding), percentage: ((safeParseFloat(longDressesWedding) - pendingDW) / safeParseFloat(longDressesWedding) * 100) },
          { category: 'Trajes de Hombre', available: totalMenSuits - pendingMS, total: totalMenSuits, percentage: ((totalMenSuits - pendingMS) / totalMenSuits * 100) },
          { category: 'Trajes de Niño', available: totalChildrenSuits - pendingCS, total: totalChildrenSuits, percentage: ((totalChildrenSuits - pendingCS) / totalChildrenSuits * 100) }
        ];

      const recentPayments = processRecentPayment(data.recentPayments);

      const birthdaysThisMonth = data.monthBirthdays.map(birthday => birthday.XVName !== "No aplica" && ({
        name: birthday.XVName,
        date: new Date(birthday.birthday).toLocaleDateString('es-DO', { day: 'numeric', month: 'short' }),
        contact: birthday.XVCellphoneNumber ?? 'No disponible'
      })).filter(Boolean);

      setDashboardData({
        totalEvents: totalEvents,
        totalMonthlyEvents: totalMonthlyEvents,
        lastMonthEvents: lastMonthEvents,
        currentMonthRevenue: currentMonthRevenue,
        lastMonthRevenue: lastMonthRevenue,
        totalRevenue: totalRevenue,
        dressInventory: dressInventory,
        suitInventory: suitInventory,
        recentEvents: recentEvents,
        monthlyRevenue: monthlyRevenue,
        eventsByType: eventsByType,
        paymentStatus: paymentStatus,
        upcomingEvents: upcomingEvents,
        inventoryStatus: inventoryStatus,
        recentPayments: recentPayments,
        birthdaysThisMonth: birthdaysThisMonth
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Componente para tarjetas de métricas
  const MetricCard = ({ title, value, icon, change, color = '#f759ab', subtitle }) => (
    <Card sx={{
      height: '100%',
      background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
      border: `1px solid ${color}30`,
      transition: 'transform 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 8px 25px ${color}20`
      }
    }}>
      <CardContent sx={{ padding: { xs: '1rem', md: '1.5rem' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Avatar sx={{
            backgroundColor: color,
            width: { xs: 40, md: 48 },
            height: { xs: 40, md: 48 }
          }}>
            {icon}
          </Avatar>
          {change && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {change > 0 ? (
                <TrendingUp sx={{ color: '#4caf50', fontSize: '1rem' }} />
              ) : (
                <TrendingDown sx={{ color: '#f44336', fontSize: '1rem' }} />
              )}
              <Typography variant="caption" sx={{
                color: change > 0 ? '#4caf50' : '#f44336',
                fontWeight: 600
              }}>
                {Math.abs(change)}%
              </Typography>
            </Box>
          )}
        </Box>
        <Typography variant="h4" sx={{
          fontWeight: 700,
          color: color,
          fontSize: { xs: '1.5rem', md: '2rem' },
          mb: 0.5
        }}>
          {typeof value === 'number' && value > 999 ?
            `${(value / 1000).toFixed(0)}K` :
            value.toLocaleString()
          }
        </Typography>
        <Typography variant="body2" sx={{
          color: 'text.secondary',
          fontWeight: 500,
          fontSize: { xs: '0.8rem', md: '0.875rem' }
        }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  // Componente para gráfico de pastel personalizado
  const CustomPieChart = ({ data, title, height = 250 }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" sx={{
          fontWeight: 600,
          color: '#5b21b6',
          mb: 2,
          fontSize: { xs: '1rem', md: '1.25rem' }
        }}>
          {title}
        </Typography>
        <ResponsiveContainer width="100%" height={height}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={isMobile ? 30 : 40}
              outerRadius={isMobile ? 70 : 90}
              paddingAngle={5}
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip formatter={(value, name) => {
              // Si es el gráfico de eventos por tipo, mostrar porcentajes
              if (name && (name.includes('XV') || name.includes('Boda') || name.includes('Alquiler'))) {
                return [`${value}%`, 'Porcentaje'];
              }
              // Para estado de pagos, mostrar cantidad
              return [value, 'Cantidad'];
            }} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );

  if (permissionError) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: '#f44336', width: 64, height: 64 }}>
            {/* Icono de candado para indicar acceso restringido */}
            <HttpsIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h5" sx={{ color: '#f44336', fontWeight: 700 }}>
            No tienes permiso para acceder a esta sección
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>
            Esta información solo está disponible para usuarios con permisos de administrador.
          </Typography>
        </Box>
      </Container>
    );
  }
  if (loading) {
    return <LoadingInformation />;
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 4,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 }
      }}>
        <Box>
          <Typography variant="h3" sx={{
            fontWeight: 700,
            color: '#5b21b6',
            fontSize: { xs: '2rem', md: '3rem' }
          }}>
            Dashboard Ejecutivo
          </Typography>
          <Typography variant="subtitle1" sx={{
            color: '#7c3aed',
            opacity: 0.8,
            fontSize: { xs: '0.9rem', md: '1rem' }
          }}>
            Resumen D'Princess Foto Films Santiago
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            onClick={fetchDashboardData}
            sx={{
              bgcolor: '#f3e8ff',
              color: '#7c3aed',
              '&:hover': { bgcolor: '#ede9fe' }
            }}
          >
            <Refresh />
          </IconButton>
          <Chip
            icon={<Today />}
            label={new Date().toLocaleDateString('es-ES', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
            sx={{
              bgcolor: '#f759ab',
              color: 'white',
              fontSize: { xs: '0.75rem', md: '0.875rem' }
            }}
          />
        </Box>
      </Box>

      {/* Métricas principales */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 4 }}>
        <Grid item xs={6} md={3} sx={{ cursor: 'pointer' }} onClick={() => navigateToPath('/events/closest-events-list')}>
          <MetricCard
            title="Eventos del mes"
            value={dashboardData.totalMonthlyEvents}
            icon={<Event />}
            change={
              dashboardData.lastMonthEvents === 0 && dashboardData.totalMonthlyEvents === 0
                ? null
                : ((dashboardData.totalMonthlyEvents - dashboardData.lastMonthEvents || 0)/(dashboardData.lastMonthEvents || 1) * 100).toFixed(2)
            }
            color="#f759ab"
            subtitle={getCurrentMonthName() + ' ' + getCurrentYear()}
          />
        </Grid>
        <Grid item xs={6} md={3} sx={{ cursor: 'pointer' }} onClick={() => navigateToPath('/events/events-list')}>
          <MetricCard
            title="Eventos Totales"
            value={dashboardData.totalEvents}
            icon={<CalendarMonthIcon />}
            //change={8}
            color="#e91e63"
            subtitle={getCurrentYear()}
          />
        </Grid>
        <Grid item xs={6} md={3} sx={{ cursor: 'pointer' }} onClick={() => navigateToPath('/billing/monthly-payment-report')}>
          <MetricCard
            title="Ingresos del mes"
            value={`$${(dashboardData.currentMonthRevenue / 1000).toFixed(0)}K`}
            icon={<AttachMoney />}
            change={
              dashboardData.lastMonthRevenue === 0 && dashboardData.currentMonthRevenue === 0
                ? null
                : Number((((dashboardData.currentMonthRevenue - dashboardData.lastMonthRevenue) / dashboardData.lastMonthRevenue || 1) * 100).toFixed(2))
            }
            color="#9c27b0"
            subtitle={getCurrentMonthName() + ' ' + getCurrentYear()}
          />
        </Grid>
        <Grid item xs={6} md={3} sx={{ cursor: 'pointer' }} onClick={() => navigateToPath('/billing/annual-payment-report')}>
          <MetricCard
            title="Ingresos anuales"
            value={`$${typeof dashboardData.totalRevenue === 'string' ? 
              (parseFloat(dashboardData.totalRevenue.replace(/[^0-9.-]+/g, '')) / 1000).toFixed(0) + 'K' : 
              (dashboardData.totalRevenue / 1000).toFixed(0) + 'K'}`}
            icon={<AccountBalance />}
            //change={-3}
            color="#673ab7"
            subtitle={getCurrentYear().toString()}
          />
        </Grid>
      </Grid>

      {/* Gráficos y estadísticas */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 4 }}>
        {/* Ingresos mensuales */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{
                fontWeight: 600,
                color: '#5b21b6',
                mb: 2,
                fontSize: { xs: '1rem', md: '1.25rem' }
              }}>
                Ingresos y Eventos Mensuales
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={dashboardData.monthlyRevenue}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" stroke="#666" />
                  <YAxis stroke="#666" />
                  <Tooltip
                    formatter={(value, name) => [
                      name === 'revenue' ? `$${(value / 1000).toFixed(0)}K` : value,
                      name === 'revenue' ? 'Ingresos' : 'Eventos'
                    ]}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#f759ab"
                    fill="#f759ab"
                    fillOpacity={0.3}
                    name="Ingresos"
                  />
                  <Area
                    type="monotone"
                    dataKey="events"
                    stroke="#e91e63"
                    fill="#e91e63"
                    fillOpacity={0.2}
                    name="Eventos"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Tipos de eventos */}
        <Grid item xs={12} lg={4}>
          <CustomPieChart
            data={dashboardData.eventsByType.length > 0 ? dashboardData.eventsByType : [
              { name: 'Sin datos', value: 100, color: '#e0e0e0' }
            ]}
            title="Distribución por Tipo de Evento"
            height={350}
          />
        </Grid>
      </Grid>

      {/* Estado de pagos y próximos eventos */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <CustomPieChart
            data={dashboardData.paymentStatus}
            title="Estado de Pagos"
            height={250}
          />
        </Grid>

        <Grid item xs={12} md={8}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
              }}>
                <Typography variant="h6" sx={{
                  fontWeight: 600,
                  color: '#5b21b6',
                  fontSize: { xs: '1rem', md: '1.25rem' }
                }}>
                  Próximos Eventos
                </Typography>
                <Chip
                  icon={<CalendarToday />}
                  label={`${dashboardData.upcomingEvents.length} eventos`}
                  size="small"
                  sx={{ bgcolor: '#f3e8ff', color: '#7c3aed' }}
                />
              </Box>
              <List sx={{ maxHeight: 250, overflow: 'auto' }}>
                {dashboardData.upcomingEvents.map((event, index) => (
                  <React.Fragment key={event.id}>
                    <ListItem>
                      <ListItemIcon>
                        <Avatar sx={{
                          bgcolor: '#f759ab',
                          width: 32,
                          height: 32,
                          fontSize: '0.8rem'
                        }}>
                          {event.date.split('-')[2] - 1}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2" sx={{
                            fontWeight: 600,
                            fontSize: { xs: '0.85rem', md: '0.9rem' }
                          }}>
                            {event.title}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" sx={{ display: 'block' }}>
                              <TodayIcon sx={{ verticalAlign: 'middle', fontSize: '14px', mr: 0.5, color: 'blue' }} />
                              {new Date(event.date).toLocaleDateString('es-ES')} - {event.time}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#666', textAlign: 'center' }}>
                              <LocationOn sx={{ verticalAlign: 'middle', fontSize: '14px', mr: 0.5, color: 'red' }} />
                              {event.location}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < dashboardData.upcomingEvents.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Estado del inventario */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{
                fontWeight: 600,
                color: '#5b21b6',
                mb: 3,
                fontSize: { xs: '1rem', md: '1.25rem' }
              }}>
                Estado del Inventario
              </Typography>
              <Grid container spacing={3}>
                {dashboardData.inventoryStatus.map((item, index) => (
                  <Grid item xs={12} sm={6} md={4} lg={2.4} key={index}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="subtitle2" sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: { xs: '0.8rem', md: '0.9rem' }
                      }}>
                        {item.category}
                      </Typography>
                      <Box sx={{ position: 'relative', display: 'inline-flex', mb: 1 }}>
                        <CircularProgress
                          variant="determinate"
                          value={item.percentage}
                          size={isMobile ? 60 : 80}
                          thickness={4}
                          sx={{ color: '#f759ab' }}
                        />
                        <Box sx={{
                          top: 0,
                          left: 0,
                          bottom: 0,
                          right: 0,
                          position: 'absolute',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <Typography variant="caption" sx={{
                            fontWeight: 600,
                            fontSize: { xs: '0.7rem', md: '0.8rem' }
                          }}>
                            {item.percentage.toFixed(1)}%
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="caption" sx={{
                        display: 'block',
                        color: 'text.secondary',
                        fontSize: { xs: '0.7rem', md: '0.75rem' }
                      }}>
                        {item.available}/{item.total} disponibles
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Pagos recientes y cumpleaños */}
      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '60vh', overflow: 'auto' }}>
            <CardContent>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
              }}>
                <Typography variant="h6" sx={{
                  fontWeight: 600,
                  color: '#5b21b6',
                  fontSize: { xs: '1rem', md: '1.25rem' }
                }}>
                  Pagos Recientes
                </Typography>
                <Chip
                  icon={<CreditCard />}
                  label="Últimos 15 días"
                  size="small"
                  sx={{ bgcolor: '#e8f5e8', color: '#4caf50' }}
                />
              </Box>
              <List>
                {dashboardData.recentPayments.map((payment, index) => (
                  <React.Fragment key={payment.id}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <Avatar sx={{
                          bgcolor: '#4caf50',
                          width: 32,
                          height: 32
                        }}>
                          <AttachMoney sx={{ fontSize: '1rem' }} />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2" sx={{
                            fontWeight: 600,
                            fontSize: { xs: '0.85rem', md: '0.9rem' }
                          }}>
                            {payment.client}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" sx={{
                              color: '#4caf50',
                              fontWeight: 600,
                              fontSize: { xs: '0.8rem', md: '0.85rem' }
                            }}>
                              ${payment.amount.toLocaleString()}
                            </Typography>
                            <Typography variant="caption">
                              {payment.method} • {payment.date}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < dashboardData.recentPayments.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '60vh', overflow: 'auto' }}>
            <CardContent>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
              }}>
                <Typography variant="h6" sx={{
                  fontWeight: 600,
                  color: '#5b21b6',
                  fontSize: { xs: '1rem', md: '1.25rem' }
                }}>
                  Cumpleaños del Mes
                </Typography>
                <Chip
                  icon={<Event />}
                  label={getCurrentMonthName() + ' ' + getCurrentYear()}
                  size="small"
                  sx={{ bgcolor: '#fff3e0', color: '#ff9800' }}
                />
              </Box>
              <List>
                {dashboardData.birthdaysThisMonth.map((birthday, index) => (
                  <React.Fragment key={index}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <Avatar sx={{
                          bgcolor: '#ff9800',
                          width: 32,
                          height: 32
                        }}>
                          <CakeIcon fontSize="small" />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2" sx={{
                            fontWeight: 600,
                            fontSize: { xs: '0.85rem', md: '0.9rem' }
                          }}>
                            {birthday.name}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" sx={{ display: 'block' }}>
                              {birthday.date}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                              <PhoneIphoneIcon sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: '14px', fontWeight: 600 }} />
                              {birthday.contact || 'No disponible'}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < dashboardData.birthdaysThisMonth.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}

export default Dashboard;
